/**
 * A KNOCK, AS A CHAT WINDOW (owner ruling 2026-08-25 #9).
 *
 * "Every chat should actually be a chat window… you also have the standard text
 * area so you can also talk to your nob and direct it." This is that window: the
 * whole history of one knock as bubbles, three named parties in it, and a
 * composer at the foot.
 *
 * WHAT THIS REPLACED. Wave 2 put the grilling behind a "What the doorman heard"
 * disclosure that opened once and shut afterwards, with a localStorage mark per
 * conversation to remember it — right, when the transcript was evidence sitting
 * above a reply box, and wrong at the door, where the hearing IS the screen and
 * a conversation that hides its own first half is not one. The marks are
 * deleted outright. What survives is the fold itself, and only where there is
 * a conversation to fold above: once somebody is through, the hearing is
 * backstory (see `HearingFold` below).
 *
 * THE OWNER'S NOB IS ON THE OWNER'S PLAN HERE (ruling R-0905-5 a). Talking to
 * your doorman about a knock is the same `message` work the desk chat meters,
 * and it wears the desk's own gate: on Free the composer is the desk's locked
 * bar (ruling R-0825-4, through `useMetered`), a plan with room answers, a
 * spent plan gets the desk's own sentence and the plan card under it. Nothing
 * about any of it is on the stream — the meter moves after
 * the reply, in the server — and the plan bar is re-read afterwards exactly as
 * it is after a desk turn. The old arm ("the knocker is paying for it") let a
 * recipient talk to a model at a stranger's expense and is gone.
 *
 * THE HEARING WITH NO READ YET (`docs/conversation-model.md` §6.2). A knock
 * still being heard opens here too: the live transcript, re-read on a clock,
 * and the same composer — what the owner says now steers the doorman's next
 * question (R-0905-5 c). What it no longer withholds is the VERBS: they are
 * offered at every moment (§3), and only the standing waits on the read.
 *
 * ONE COMPOSER, TWO ADDRESSEES (owner, 2026-08-26). The wave above got the
 * window right and the address wrong: the box talked to the owner's Nob on
 * EVERY knock, including the ones they had already answered, so an owner who
 * replied and then wrote again was answered by their own doorman. The stage
 * decides now — at the door it is the Nob, and once you have let somebody
 * through it is them — and the hearing folds away above the conversation,
 * because a thread between two people should open on what they said to each
 * other rather than on how one of them got in.
 *
 * A conversation between two people is not AI work and nothing here meters it.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  attachmentUrl,
  isAudioAttachment,
  isHouseSide,
  streamOwnerChat,
  submitInboxAction,
  type Attachment,
  type GateVerbHint,
  type InboxTranscriptLine,
} from "../../lib/api";
import type { Conversation } from "../../lib/conversations";
import { linkify } from "../../lib/linkify";
import { attachmentLine, Transcript, voiceNoteLabel } from "../knock/attachment";
import { AudioBubble } from "../knock/audio-bubble";
import { planQuery, qk } from "../../lib/queries";
import { useRoomAdded } from "../../lib/room";
import { BROKE_COPY, LockedComposer } from "../doorman/doorman-area";
import { SpentRow } from "../plan/usage-notice";
import { CHAT_FIELD, Composer, Gutter, NobBubbleFace, SystemRow } from "../knock/chat";
import { runAt, runPositions } from "../../lib/runs";
import { BLOCK_VERB, NOT_FOR_ME, OPEN_THE_DOOR, VerbButton } from "./knock-card";
import { RELAY_FAILED_NOTE } from "./use-inbox-actions";
import { staleAfterKnockChange } from "../shell/desk";
import { ConversationFace } from "../shell/mascot";
import { useShellUi } from "../shell/state";
import { useMetered } from "../shell/use-metered";

export const COMPOSER_PLACEHOLDER = "Talk to your Nob about this knock";
export const COMPOSER_LABEL = "Talk to your Nob about this knock";
/** The typing line — it names WHO is being answered, because two Nobs speak here. */
export const REPLYING_COPY = "Nob is replying to you…";
export const HEARING_DIVIDER = "the hearing";
export const TO_YOU_TAG = "to you";
export const CHAT_FAILED = "Your Nob lost the thread. Say that again.";

/** The person composer's copy, once the door is open. `Message Ada Lovelace`. */
export function personPlaceholder(name: string): string {
  return `Message ${name}`;
}
export const PERSON_SEND_LABEL = "Send";
export const PERSON_FAILED = "That did not reach them. Say it again.";
/**
 * The bar the grilling folds under, once there is a conversation below it —
 * and it is R-0825-5's own words, on both sides of the door (R-0910-7 rule 2).
 *
 * It read "The hearing" until the sender's room grew a fold of its own and the
 * two came out different, which is how one object becomes two: the same block,
 * over the same transcript, named differently depending on who is looking at
 * it. The ruling names it, so the ruling's words win.
 */
export const HEARING_FOLD = "What the doorman heard";
/** The caret riding the end of a sentence still being said. Same block the hearing uses. */
export const CARET = "▍";

/** What the owner is called in their own conversation. */
export const YOU = "You";

/**
 * WHAT THE OWNER'S DESK SAYS OVER A RELAY (ruling R-0910-11 rule 3).
 *
 * The same row the sender reads as a plain doorman question — because on their
 * side the doorman thought of it, and nothing there may say otherwise. Here it
 * is the owner's own instruction having been carried out, so the desk says so:
 * they asked for a question to be put, and this is it, quoted. Without the line
 * the owner cannot tell an obeyed instruction from a dropped one, and the gate
 * drops silently on purpose (`rules/sayGate.ts`).
 *
 * It sits in the ROOM'S voice — the centred notice, not the Nob's bubble —
 * because it is the desk reporting a fact, not the Nob speaking to them.
 */
export function askedThem(text: string): string {
  return `Asked them: "${text}"`;
}

/**
 * One live line — something said in this sitting, before the knock is re-read.
 * They are dropped the moment the server's copy lands, or the turn renders
 * twice: once as state, once as history.
 */
type LiveLine =
  | { kind: "owner"; content: string }
  /**
   * `verb` is the gate move the owner asked for on the turn this line answers
   * (R-0910-13 rule 3). The Nob cannot make it and has just said so; the button
   * that can rides under the answer, so the honest reply costs one tap.
   */
  | { kind: "nob"; content: string; verb: GateVerbHint | null }
  /** A question the Nob was asked to put, and did (R-0910-11). */
  | { kind: "relay"; content: string };

/** The card's three door verbs, in the words the card uses for them. */
const VERB_LABEL: Record<GateVerbHint, string> = {
  let_in: OPEN_THE_DOOR,
  decline: NOT_FOR_ME,
  block: BLOCK_VERB,
};

/**
 * A named bubble. The name is ABOVE the bubble rather than inside it: three
 * parties in one column need attribution that can be read at a glance, and a
 * colour alone cannot carry three.
 *
 * FACES MOVED BESIDE THE BUBBLES (wave 90). A left-hand bubble — the knocker,
 * or a Nob — sits behind the face gutter, and the face stands in it beside the
 * LAST bubble of its run (`lib/runs.ts`); the name and its chip stand above the
 * FIRST, without the face they used to carry. The owner's own bubbles are
 * unchanged: "YOU", on the right, and no face.
 */
function Bubble({
  name,
  gutter,
  label = true,
  tag,
  side,
  toOwner = false,
  children,
}: {
  name: string;
  /** Left side only: the speaker's face on a run's last bubble, null on the others. */
  gutter?: React.ReactNode | null;
  /** The name and its chip — above a run's first bubble only. */
  label?: boolean;
  tag?: string;
  side: "left" | "right";
  /** The Nob answering the OWNER rather than the hearing — accent paper, "to you". */
  toOwner?: boolean;
  children: React.ReactNode;
}) {
  const right = side === "right";
  const column = (
    <div className={`flex min-w-0 flex-1 flex-col gap-1 ${right ? "items-end" : "items-start"}`}>
      {label && (
        <span
          className={`flex items-center gap-1.5 text-[11px] uppercase tracking-[0.08em] text-fainter ${
            right ? "flex-row-reverse" : ""
          }`}
        >
          <span>{name}</span>
          {tag && (
            <span className="rounded-full border border-accent/50 px-1.5 py-px text-[10px] normal-case tracking-normal text-accent-ink">
              {tag}
            </span>
          )}
        </span>
      )}
      <div
        data-bubble={right ? "me" : "them"}
        data-to-owner={toOwner ? "true" : undefined}
        className={
          right
            ? "max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-md bg-accent px-4 py-2.5 text-[15px] leading-relaxed text-accent-fg"
            : toOwner
              ? "max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-bl-md border border-accent/40 bg-accent/10 px-4 py-2.5 text-[15px] leading-relaxed text-text"
              : "max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-bl-md bg-bubble-them px-4 py-2.5 text-[15px] leading-relaxed text-bubble-them-fg"
        }
      >
        {children}
      </div>
    </div>
  );
  if (right || gutter === undefined) return <div className="nob-enter flex">{column}</div>;
  return (
    <div className="nob-enter flex items-end gap-2">
      <Gutter face={gutter} />
      {column}
    </div>
  );
}

/**
 * Who spoke a line, for the runs (wave 90). The owner is the reader; the room's
 * notices and a relayed question ("Asked them: …") belong to nobody and break a
 * run. Nob in the hearing and Nob answering the owner are two parties, because
 * they wear two labels.
 */
function partyOf(line: InboxTranscriptLine): string | null {
  if (line.relayed && line.role === "doorman") return null;
  if (line.role === "notice") return null;
  if (line.role === "sender") return "sender";
  if (line.role === "owner" || line.role === "recipient") return "you";
  return isHouseSide(line.role) ? "nob-to-you" : "nob";
}

/**
 * The end of the hearing, drawn (the ruling's own divider). Above it is the
 * grilling — the doorman and the stranger, before either reached this screen.
 * Below it is everything that happened once the knock arrived at the door.
 */
function HearingDivider() {
  return (
    <div data-divider="hearing" className="flex items-center gap-3 py-1" aria-hidden>
      <span className="h-px flex-1 border-t border-dashed border-border" />
      <span className="text-[11px] uppercase tracking-[0.1em] text-fainter">{HEARING_DIVIDER}</span>
      <span className="h-px flex-1 border-t border-dashed border-border" />
    </div>
  );
}

/**
 * WHERE THE DIVIDER GOES, computed rather than stored: after the last thing said
 * IN the hearing. The two grilling roles are `sender` and `doorman`; everything
 * else in the transcript — the notices an action wrote, the owner's own words,
 * their Nob's answers — happened after the knock arrived at the door.
 *
 * `-1` when there is nothing after the hearing, and the divider is not drawn:
 * a line marking the end of the only thing on screen marks nothing.
 */
export function hearingEndsAt(lines: InboxTranscriptLine[]): number {
  let last = -1;
  for (let i = 0; i < lines.length; i++) {
    const role = lines[i]?.role;
    if (role === "sender" || role === "doorman") last = i;
  }
  return last >= 0 && last < lines.length - 1 ? last : -1;
}

/**
 * WHERE THE FOLD GOES, in a thread whose door has opened: at the first
 * `recipient` line — the reply that opened it.
 *
 * Everything above that line happened while this person was still standing
 * outside: the grilling, the notices, and any remarks the owner made to their
 * own Nob about them. Everything from it down is the two of them talking. That
 * is a cleaner cut than `hearingEndsAt` can make and it is why this is a second
 * function rather than a smarter version of the first: once the sender can
 * write back, the LAST `sender` line is no longer the end of the hearing, and a
 * divider computed that way would slide down the thread with every message they
 * send.
 *
 * `-1` when there is no reply in the transcript, or when the reply IS the first
 * line — a fold over nothing is a control that does nothing.
 */
export function hearingFoldsAt(lines: InboxTranscriptLine[]): number {
  const at = lines.findIndex((line) => line.role === "recipient");
  return at > 0 ? at : -1;
}

/**
 * The grilling, folded. A native `<details>` because that is exactly this
 * control: closed by default, opens on a tap or on Enter, tells a screen reader
 * what it is without a single aria attribute, and — the part a hand-rolled
 * disclosure loses — its contents stay in the document, so browser find-in-page
 * and every test that reads the thread can still see the hearing.
 */
function HearingFold({ children }: { children: React.ReactNode }) {
  return (
    <details data-fold="hearing" className="rounded-2xl border border-dashed border-border">
      <summary
        className="cursor-pointer list-none px-4 py-2 text-[11px] uppercase tracking-[0.1em]
                   text-fainter hover:text-muted focus-visible:outline-none focus-visible:ring-2
                   focus-visible:ring-focus/70"
      >
        {HEARING_FOLD}
      </summary>
      <div className="flex flex-col gap-3 px-3 pb-3">{children}</div>
    </details>
  );
}

export function KnockChat({
  conv,
  doorLive,
  transcript,
  attachments = [],
  knockId,
  above,
  verbs,
  status = null,
  onSettled,
}: {
  conv: Conversation;
  doorLive: boolean;
  transcript: InboxTranscriptLine[] | null;
  /**
   * What the sender put into the hearing — a voice note plays here and reads
   * here (R-0905-4), on the sender's side of the thread. A file is not a turn
   * and carries no sequence, so the files sit at the end of the hearing rather
   * than at an invented position inside it — the same rule the sender's own
   * re-entry follows (`knock/started.tsx`).
   */
  attachments?: Attachment[];
  knockId: string;
  /**
   * What sits at the head of the thread, above the first bubble — the knock's
   * card, with the standing and the verbs. It scrolls with the conversation
   * rather than pinning above it: the card is the FIRST thing said about this
   * person, and a summary nailed to the top of a chat window is a summary the
   * owner has to look past for the rest of the conversation.
   *
   * IN A THROUGH-THREAD IT GOES UNDER THE FOLD (owner item 23, 2026-08-26), at
   * the top of the hearing it belongs to. Same reasoning as the hearing's own:
   * the standing, the receipts and the rank are how this person got in, which
   * is backstory once they are in — and the card's verbs are gone by then
   * anyway (`knock-card.tsx`), so what is left is a plate, and a plate belongs
   * with the rest of the record rather than on top of a conversation.
   */
  above?: React.ReactNode;
  /**
   * THE CARD'S OWN DOOR VERBS (R-0910-13 rule 3), handed down so the answer that
   * says "only you can" can carry the button that does it. They are the card's
   * handlers and nothing else: pressing one opens the card's own confirm — the
   * reply composer, the dismissal's chips, the block's "are you sure" — because
   * this is the same action offered a second time, not a second road to it.
   */
  verbs?: { onOpenDoor: () => void; onNotForMe: () => void; onBlock: () => void };
  /**
   * WHAT THE LAST VERB DID, AND ITS UNDO — UNDER THE COMPOSER (F-58, 17b).
   *
   * It used to ride on the card in `above`, which is exactly where it could not
   * be read: a reply opens the door, the door turns this into a through-thread,
   * and `above` goes under the closed fold in the same frame. The composer is
   * the one thing on this surface that never folds and never scrolls away, so
   * the confirmation lives with it. Passed in rather than computed here — the
   * verbs and their undo window belong to the shell's one copy of them
   * (`shell/desk.ts`), and this pane is not where that state lives.
   */
  status?: React.ReactNode;
  /** The turn landed and was persisted: re-read the knock. */
  onSettled: () => void;
}) {
  const queryClient = useQueryClient();
  const ui = useShellUi();
  /**
   * The desk's gate, from the desk's one definition (R-0905-5 a, R-0825-4):
   * `false` is a tier that does no AI work, and the composer is the locked
   * bar; `null` is nobody has said yet, and the box waits rather than flashing
   * an upsell at a subscriber on a cold load.
   */
  const metered = useMetered();
  const [draft, setDraft] = useState("");
  const [live, setLive] = useState<LiveLine[]>([]);
  const [streaming, setStreaming] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * The error is the plan's refusal (wave 57): its sentence gets the one
   * control beside it, "Get more usage", and room arriving takes both away.
   * The plan read is only for that control's label, which is the server's.
   */
  const [spent, setSpent] = useState(false);
  const plan = useQuery(planQuery());
  useRoomAdded(() => {
    if (!spent) return;
    setSpent(false);
    setError(null);
  });

  const busyRef = useRef(false);
  useEffect(() => {
    busyRef.current = busy;
  }, [busy]);
  const abort = useRef<AbortController | null>(null);
  useEffect(() => () => abort.current?.abort(), []);

  /** See the note on the effect that reads it, below. */
  const settledLength = useRef<number | null>(null);

  /**
   * A new knock is a new conversation: nothing said about the last one may
   * survive into it. Without this, opening a second knock shows the first one's
   * live lines under the second one's history.
   */
  useEffect(() => {
    abort.current?.abort();
    settledLength.current = null;
    setDraft("");
    setLive([]);
    setStreaming(null);
    setBusy(false);
    setError(null);
    setSpent(false);
  }, [knockId]);

  /**
   * FOLLOW THE CONVERSATION, NEVER THE OPENING. The pane opens at the top —
   * the card, the verdict, the verbs, which is what an owner came for — and
   * only chases the foot while something is being said. A window that scrolled
   * to the composer on open would hide the knock behind its own transcript.
   */
  const foot = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (busy) foot.current?.scrollIntoView({ block: "end" });
  });

  /**
   * WHEN THE LIVE LINES YIELD TO THE SERVER'S COPY, and why it is a length and
   * not a timer.
   *
   * A turn ends by asking for the knock to be re-read; the re-read is somebody
   * else's promise and lands whenever it lands. Dropping the live lines at the
   * moment the request goes out would blank what the owner just said and what
   * their Nob just replied, for as long as the round trip takes — and forever,
   * if the re-read fails. So the baseline is the transcript's length at the
   * moment the turn settled, and the live copies stand until the server's own
   * transcript is longer than that. Late is fine here; missing is not.
   */
  const length = transcript?.length ?? 0;
  useEffect(() => {
    if (settledLength.current !== null && length > settledLength.current) {
      settledLength.current = null;
      setLive([]);
    }
  }, [length]);
  const lengthRef = useRef(length);
  lengthRef.current = length;

  /**
   * WHO THE COMPOSER IS TALKING TO, and it is the GATE that decides.
   *
   * The stage is the gate (`docs/conversation-model.md` §6.3), so this reads
   * `let_in` and nothing else: at the door — or after a decline, where the
   * gatekeeper is still standing (§5.2) — the box talks to your Nob, because
   * the person outside has not been let in and there is nobody in here to
   * address. Once you have opened the door they ARE in, the doorman has stepped
   * aside permanently (§3), and the box talks to them.
   *
   * It used to read `replied`, which is what the owner types AFTER the door is
   * open rather than what opens it — the second half of the bug the owner hit:
   * they replied, wrote again, and their Nob answered.
   */
  const through = conv.stage === "through";

  /**
   * The owner writing TO THE PERSON. Not a stream and not a turn — one POST,
   * the same Reply verb the card carries, which is what "letting through" is
   * recorded as and stays recorded as for every message after the first.
   *
   * The bubble goes up BEFORE the request and stays up unless the request
   * fails. That is the "it didn't save my reply" fix seen from the client end:
   * the owner's words are on their screen the instant they press send, and the
   * server's own copy replaces them when the re-read lands.
   *
   * IT REFRESHES WHAT THE VERBS REFRESH, ON BOTH ROADS (2026-09-09). Sending
   * here is a verb's worth of change — the gate may have just moved, the
   * thread's last line certainly has — and this used to re-read the open knock
   * and nothing else, so the sidebar's row stayed at "waiting on you" while the
   * conversation ran. And on FAILURE it refreshes too, because the server may
   * have moved the gate before whatever later step failed: the screen finds out
   * what is actually true, and then says the message did not go.
   */
  const sendToPerson = useCallback(() => {
    const message = draft.trim();
    if (!message || busyRef.current) return;
    setBusy(true);
    setError(null);
    setDraft("");
    const ownerLine: LiveLine = { kind: "owner", content: message };
    setLive((prev) => [...prev, ownerLine]);

    void (async () => {
      const result = await submitInboxAction({
        knock_id: knockId,
        action: "reply",
        note: message,
      });
      if (!result.ok) {
        setDraft((current) => (current.trim() ? current : message));
        setLive((prev) => prev.filter((line) => line !== ownerLine));
        setBusy(false);
        staleAfterKnockChange(queryClient);
        onSettled();
        setError(PERSON_FAILED);
        return;
      }
      setBusy(false);
      settledLength.current = lengthRef.current;
      staleAfterKnockChange(queryClient);
      onSettled();
      // The line IS in the thread — it stays where the optimistic render put it
      // — and the masked copy is what did not go (F-58, ruling R-0914-2). Said
      // as a system row rather than as a failure, because the send succeeded.
      if (result.reply_sent === false) setError(RELAY_FAILED_NOTE);
    })();
  }, [draft, knockId, onSettled, queryClient]);

  const sendToNob = useCallback(() => {
    const message = draft.trim();
    if (!message || busyRef.current) return;
    setBusy(true);
    setError(null);
    setSpent(false);
    setDraft("");
    // The owner's line goes up as THIS object, so a failed turn takes exactly
    // it back out rather than whatever happens to sit last.
    const ownerLine: LiveLine = { kind: "owner", content: message };
    setLive((prev) => [...prev, ownerLine]);

    const controller = new AbortController();
    abort.current = controller;

    void (async () => {
      let spoken = "";
      let settled = false;
      let failure: string | null = null;
      let exhausted = false;
      let verb: GateVerbHint | null = null;
      const relays: LiveLine[] = [];
      for await (const event of streamOwnerChat(knockId, message, controller.signal)) {
        if (event.type === "token") {
          spoken += event.delta;
          setStreaming(spoken);
        }
        if (event.type === "relay") relays.push({ kind: "relay", content: event.content });
        if (event.type === "done") {
          settled = true;
          verb = event.verb_hint;
        }
        if (event.type === "error") {
          // The desk's own refusal, in the desk's own words (R-0905-5 a): a
          // spent plan with nothing behind it. The words go back in the box.
          exhausted = event.code === "PLAN_EXHAUSTED";
          failure = exhausted ? BROKE_COPY : (event.message ?? CHAT_FAILED);
        }
      }
      // A stream that stopped without `done` did not take the turn — the same
      // guard the hearing and the doorman thread keep.
      if (failure === null && !settled && !controller.signal.aborted) failure = CHAT_FAILED;
      if (failure !== null) {
        setDraft((current) => (current.trim() ? current : message));
        setLive((prev) => prev.filter((line) => line !== ownerLine));
        setStreaming(null);
        setBusy(false);
        setError(failure);
        setSpent(exhausted);
        return;
      }
      setLive((prev) => [...prev, { kind: "nob", content: spoken.trim(), verb }, ...relays]);
      setStreaming(null);
      setBusy(false);
      // The server's copy is the real one; these stand in for it until the
      // re-read is demonstrably longer than the transcript they were said over.
      settledLength.current = lengthRef.current;
      onSettled();
      // The meter moved on the OWNER'S plan (R-0905-5 a): the usage ring reads
      // the plan first and the desk's thread second, so both are re-read — the
      // same movement a desk turn shows, from the same two keys.
      void queryClient.invalidateQueries({ queryKey: qk.plan() });
      void queryClient.invalidateQueries({ queryKey: qk.doormanThread() });
    })();
  }, [draft, knockId, onSettled, queryClient]);

  const lines = transcript ?? [];
  const senderName = conv.name;

  /**
   * The fold, or the divider — never both, and the stage picks.
   *
   * A thread still at the door is the hearing, in full, inline: it is the only
   * thing that has happened and it is what the owner came to read. A thread
   * that has been let through is a conversation between two people, and the
   * hearing is now the backstory to it — present, one tap away, and not in the
   * way of the messages.
   */
  const foldAt = through ? hearingFoldsAt(lines) : -1;
  const dividerAfter = foldAt < 0 ? hearingEndsAt(lines) : -1;
  /**
   * Where the hearing stops, for the files to sit after: the fold when there
   * is one, the divider when there is one, the end of the thread otherwise.
   */
  const hearingEnd = foldAt > 0 ? foldAt : dividerAfter >= 0 ? dividerAfter + 1 : lines.length;

  /**
   * A VOICE NOTE SENT AS A TURN IS ONE LINE (wave 78, as the knocker's thread
   * does since wave 76). The line names the note it was said in, so it draws
   * as the note — the player, the words under it — and the note is not drawn
   * a second time among the files at the end of the hearing.
   */
  const notes = new Map(attachments.map((a) => [a.attachment_id, a]));
  const said = new Set(lines.map((line) => voiceNoteOf(line, notes)?.attachment_id));
  const files = attachments.filter((attachment) => !said.has(attachment.attachment_id));

  /**
   * The live half runs on its own (wave 90): it always opens on the owner's
   * own line, so nothing it draws continues a run from the transcript above.
   */
  const liveRuns = runPositions([
    ...live.map((line) => ({
      party: line.kind === "relay" ? null : line.kind === "owner" ? "you" : "nob-to-you",
      at: null,
    })),
    ...(streaming !== null ? [{ party: "nob-to-you", at: null }] : []),
  ]);
  const nobGutter = (last: boolean | undefined): React.ReactNode | null =>
    last === false ? null : <NobBubbleFace />;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6">
        <div className="mx-auto flex w-full max-w-2xl flex-col gap-3">
          {foldAt > 0 ? (
            <>
              <HearingFold>
                {above}
                <KnockLines
                  lines={lines.slice(0, foldAt)}
                  conv={conv}
                  doorLive={doorLive}
                  notes={notes}
                  files={files}
                />
              </HearingFold>
              <KnockLines
                lines={lines.slice(foldAt)}
                conv={conv}
                doorLive={doorLive}
                notes={notes}
              />
            </>
          ) : (
            <>
              {above}
              {/* The hearing's files sit after its last line; the divider is
                  drawn after the files when there are any (R-0905-4), and by
                  the lines themselves when there are none. */}
              <KnockLines
                lines={lines.slice(0, hearingEnd)}
                conv={conv}
                doorLive={doorLive}
                notes={notes}
                files={files}
                dividerAfter={files.length === 0 ? dividerAfter : -1}
              />
              {dividerAfter >= 0 && files.length > 0 && <HearingDivider />}
              <KnockLines
                lines={lines.slice(hearingEnd)}
                conv={conv}
                doorLive={doorLive}
                notes={notes}
              />
            </>
          )}

          {live.map((line, i) =>
            line.kind === "relay" ? (
              <SystemRow key={`live-${i}`}>{askedThem(line.content)}</SystemRow>
            ) : line.kind === "owner" ? (
              <Bubble key={`live-${i}`} name={YOU} side="right">
                {line.content}
              </Bubble>
            ) : (
              <div key={`live-${i}`} className="flex flex-col items-start gap-2">
                <Bubble
                  name="Nob"
                  gutter={nobGutter(liveRuns[i]?.last)}
                  label={liveRuns[i]?.first ?? true}
                  side="left"
                  toOwner
                  tag={TO_YOU_TAG}
                >
                  {line.content}
                </Bubble>
                {/* The verb the owner asked for, a tap away (R-0910-13 rule 3).
                    The Nob has just told them only they can do it; the button
                    that does it is the card's, and so is what happens next. */}
                {line.verb && verbs && (
                  <VerbButton
                    primary={line.verb === "let_in"}
                    locked={false}
                    onClick={
                      line.verb === "block"
                        ? verbs.onBlock
                        : line.verb === "decline"
                          ? verbs.onNotForMe
                          : verbs.onOpenDoor
                    }
                  >
                    {VERB_LABEL[line.verb]}
                  </VerbButton>
                )}
              </div>
            ),
          )}

          <div aria-live="polite" className="contents">
            {streaming !== null && (
              <Bubble
                name="Nob"
                gutter={<NobBubbleFace />}
                label={liveRuns[live.length]?.first ?? true}
                side="left"
                toOwner
                tag={TO_YOU_TAG}
              >
                {streaming}
                <span className="nob-breathe ml-px text-accent-ink/90">{CARET}</span>
              </Bubble>
            )}
            {/* "Nob is replying to you" is a lie in a through-thread — nobody
                is composing an answer, a POST is in flight to a person who may
                read it tomorrow. So the waiting line belongs to the at-door
                composer only, and the send button's own disabled state is what
                says something is happening here. */}
            {!through && busy && streaming === null && (
              <SystemRow breathe>{REPLYING_COPY}</SystemRow>
            )}
            {error &&
              (spent ? (
                <SpentRow
                  line={error}
                  plan={plan.data?.plan}
                  onAction={() => ui.openBilling("plans", "options")}
                />
              ) : (
                <SystemRow>{error}</SystemRow>
              ))}
          </div>
          <div ref={foot} />
        </div>
      </div>

      {/* The standard text area, pinned where every composer in this product
          sits. Two people talking is never gated; talking to your Nob is the
          owner's AI work and wears the desk's gate (R-0905-5 a): the locked
          bar on Free, and nothing at all until the plan has answered. */}
      <div className="flex-none border-t border-border-soft bg-bg px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
        <div className="mx-auto w-full max-w-2xl">
          {!through && metered === false ? (
            <LockedComposer
              onSeePlans={() => ui.openBilling("plans")}
              trial={plan.data?.trial ?? null}
              onStartTrial={() => ui.openBilling("plans", "trial")}
            />
          ) : !through && metered === null ? null : (
            <KnockComposer
              value={draft}
              onChange={setDraft}
              onSend={through ? sendToPerson : sendToNob}
              busy={busy}
              {...(through ? { addressee: senderName } : {})}
            />
          )}
          {/* Directly beneath it, on every one of the three composers above —
              the locked bar included, because a verb pressed on Free still has
              thirty seconds to be taken back. */}
          {status}
        </div>
      </div>
    </div>
  );
}

/**
 * THE LINES OF ONE KNOCK, ATTRIBUTED — the stranger by name and face, Nob by
 * its own, the room's notices centred, the owner on the right. The chat window
 * draws its live knock with this, and there is only ever one knock to draw:
 * one row per direction, one transcript, top to bottom.
 *
 * `dividerAfter` is the index the hearing ends at, when the caller wants the
 * dashed divider drawn; `-1` draws none. `notes` are the hearing's files by
 * id: a knocker's line said as a voice note draws as that note.
 */
export function KnockLines({
  lines,
  conv,
  doorLive,
  notes,
  files = [],
  dividerAfter = -1,
}: {
  lines: InboxTranscriptLine[];
  conv: Conversation;
  doorLive: boolean;
  notes?: ReadonlyMap<string, Attachment>;
  /**
   * The hearing's files, drawn after its last line as the knocker's own
   * bubbles (R-0905-4) — here rather than by the caller, so they join the
   * knocker's run and the face goes beside the last of them (wave 90).
   */
  files?: readonly Attachment[];
  dividerAfter?: number;
}) {
  const senderName = conv.name;
  const senderFace = <ConversationFace conv={conv} doorLive={doorLive} size="bubble" person />;
  const runs = runPositions([
    ...lines.map((line) => ({ party: partyOf(line), at: runAt(line.created_at) })),
    // A file carries no moment here; it sits where the hearing ends.
    ...files.map(() => ({ party: "sender", at: null })),
  ]);
  /** The face in a left bubble's gutter: on the last of its run, and nowhere else. */
  const gutter = (i: number, face: React.ReactNode): React.ReactNode | null =>
    runs[i]?.last ? face : null;
  const first = (i: number): boolean => runs[i]?.first ?? true;
  return (
    <>
      {lines.map((line, i) => (
        <div key={`${line.seq}-${line.role}`} className="contents">
          {/* The question the owner asked their Nob to put (R-0910-11 rule 3).
              It is a `doorman` row and the sender reads it as one; the mark is
              tested BEFORE the roles because on this side of the door the
              interesting fact about it is not who spoke but who asked. */}
          {line.relayed && line.role === "doorman" ? (
            <SystemRow>{askedThem(line.content)}</SystemRow>
          ) : line.role === "notice" ? (
            <SystemRow>{line.content}</SystemRow>
          ) : line.role === "sender" ? (
            <Bubble name={senderName} gutter={gutter(i, senderFace)} label={first(i)} side="left">
              <SenderSaid line={line} notes={notes} />
            </Bubble>
          ) : line.role === "owner" || line.role === "recipient" ? (
            // Both are the owner speaking, so both wear their name and their
            // side. No mark tells them apart, and that is the decision rather
            // than an oversight: an `owner` row can only have come from the
            // at-door composer, which exists only before a reply, so in a
            // through-thread they sit under the fold with the rest of the
            // backstory and the `recipient` rows sit below it. The thread says
            // which is which by where it puts them.
            <Bubble name={YOU} side="right">
              {linkify(line.content)}
            </Bubble>
          ) : (
            <Bubble
              name="Nob"
              gutter={gutter(i, <NobBubbleFace />)}
              label={first(i)}
              side="left"
              toOwner={isHouseSide(line.role)}
              {...(isHouseSide(line.role) ? { tag: TO_YOU_TAG } : {})}
            >
              {linkify(line.content)}
            </Bubble>
          )}
          {i === dividerAfter && <HearingDivider />}
        </div>
      ))}
      {files.map((attachment, j) => (
        <Bubble
          key={attachment.attachment_id}
          name={senderName}
          gutter={gutter(lines.length + j, senderFace)}
          label={first(lines.length + j)}
          side="left"
        >
          {isAudioAttachment(attachment) ? (
            <VoiceNote note={attachment} />
          ) : (
            attachmentLine(attachment)
          )}
        </Bubble>
      ))}
    </>
  );
}

/**
 * The voice note a knocker's line was said in, when it is one and it is here —
 * otherwise nothing, and the line draws as its words.
 */
function voiceNoteOf(
  line: InboxTranscriptLine,
  notes: ReadonlyMap<string, Attachment>,
): Attachment | undefined {
  if (line.role !== "sender" || !line.voice_attachment_id) return undefined;
  const note = notes.get(line.voice_attachment_id);
  return note && isAudioAttachment(note) ? note : undefined;
}

/** A knocker's line: the voice note it was said in, or else its words. */
function SenderSaid({
  line,
  notes,
}: {
  line: InboxTranscriptLine;
  notes: ReadonlyMap<string, Attachment> | undefined;
}) {
  const note = notes && voiceNoteOf(line, notes);
  return note ? <VoiceNote note={note} /> : <>{linkify(line.content)}</>;
}

/** A knocker's voice note: the player, and the words under it (R-0905-4). */
function VoiceNote({ note }: { note: Attachment }) {
  return (
    <>
      <AudioBubble
        src={attachmentUrl(note.attachment_id)}
        durationMs={note.duration_ms}
        label={voiceNoteLabel(note)}
      />
      <Transcript text={note.transcript} />
    </>
  );
}

/**
 * The standard text area, at the foot of the thread — AND IT ADDRESSES ONE OF
 * TWO PEOPLE, depending on whether the door has opened.
 *
 * At the door it speaks to the owner's own Nob, and it coexists with the action
 * verbs on the card above: Reply is a verb up there and this is a conversation
 * down here, and merging them would make "send" ambiguous about who is about to
 * read it. Once the owner has replied there is no ambiguity left to protect —
 * the person is in, this is the box you talk to them in, and `addressee` names
 * them in the placeholder so it cannot be mistaken for the other one.
 */
export function KnockComposer({
  value,
  onChange,
  onSend,
  busy,
  addressee,
}: {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  busy: boolean;
  /** Present only in a through-thread: whose name the box is addressed to. */
  addressee?: string;
}) {
  const toPerson = addressee !== undefined;
  const label = toPerson ? personPlaceholder(addressee) : COMPOSER_LABEL;
  return (
    <Composer
      onSubmit={onSend}
      disabled={busy || value.trim() === ""}
      sendLabel={toPerson ? PERSON_SEND_LABEL : "Send to your Nob"}
    >
      <label htmlFor="nob-knock-chat" className="sr-only">
        {label}
      </label>
      <textarea
        id="nob-knock-chat"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          // Enter sends; shift-Enter is a new line. The same bargain every
          // composer in this product makes.
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            onSend();
          }
        }}
        rows={1}
        placeholder={toPerson ? personPlaceholder(addressee) : COMPOSER_PLACEHOLDER}
        className={`${CHAT_FIELD} max-h-40 resize-none`}
      />
    </Composer>
  );
}
